import { useCallback } from 'react';

import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { faArrowUpArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Download } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { FilterExpandableButton } from '../../../../../../components/molecules/FilterExpandableButton';
import DataViewToolbar from '../../components/TableToolbar';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import Button from '@/components/atoms/Button';
import Dropdown from '@/components/molecules/Dropdown';
import ExpandableButton from '@/components/molecules/ExpandableButton';
import NaturalLanguageSearchBox from '@/components/molecules/NaturalLanguageSearchBox';
import Pagination from '@/components/organisms/Pagination';

interface DocumentEditorToolbarProps {
  openSettingsModal: () => void;
  openDownloadModal: () => void;
  openDownloadZipModal: () => void;
  openDownloadAllZipModal: () => void;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (value: string, useNaturalLanguage: boolean) => void;
  clearSearch: () => void;
  processingNL?: boolean;
  currentPage: number;
  totalPages?: number;
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (page: number) => void;
  setFiltersModalOpen: (open: boolean) => void;
  filterCount: number;
  setSortModalOpen: (open: boolean) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  hasActiveFilters: boolean;
}

const DocumentEditorToolbar = ({
  openSettingsModal,
  openDownloadModal,
  openDownloadZipModal,
  openDownloadAllZipModal,
  globalFilter,
  setGlobalFilter,
  onSearchCall,
  clearSearch,
  currentPage,
  totalPages,
  prevPage,
  nextPage,
  goToPage,
  setFiltersModalOpen,
  filterCount,
  setSortModalOpen,
  pageSize,
  onPageSizeChange,
  hasActiveFilters,
  processingNL,
}: DocumentEditorToolbarProps) => {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const handleNLSearchExecuted = useCallback(
    (trigger: 'wand' | 'spacebar') => {
      if (trigger === 'wand') {
        trackCustomEvent(TrackingEvents.NaturalLanguageSearchDocumentsWand, { caseId });
      } else {
        trackCustomEvent(TrackingEvents.NaturalLanguageSearchDocumentsSpacebar, { caseId });
      }
    },
    [caseId],
  );

  return (
    <DataViewToolbar
      titleSection={<h1 className="text-lg font-semibold">Documents</h1>}
      actionButtons={
        <>
          <Button
            onClick={() => {
              trackCustomEvent(TrackingEvents.ClickAddDocuments);
              openSettingsModal();
            }}
            className="bg-buttonSecondary hover:bg-buttonSecondary-hover rounded border px-2 py-2 text-xs"
            text="Add Documents"
            icon={<FontAwesomeIcon icon={faPlusCircle} className="pr-0 xl:pr-2" />}
            hideTextSmallScreen
          />

          <Dropdown
            id="download-dropdown"
            placeholder="Download"
            buttonIcon={<Download className="h-4 w-4" />}
            options={
              hasActiveFilters
                ? [
                    { label: 'Download Filtered List', key: 'list' },
                    { label: 'Download Filtered Zip', key: 'filtered-zip' },
                    { label: 'Download All Zip', key: 'all-zip' },
                  ]
                : [
                    { label: 'Download List', key: 'list' },
                    { label: 'Download Zip', key: 'all-zip' },
                  ]
            }
            onOptionClick={(key) => {
              if (key === 'list') {
                openDownloadModal();
              } else if (key === 'filtered-zip') {
                openDownloadZipModal();
              } else if (key === 'all-zip') {
                openDownloadAllZipModal();
              }
            }}
            className="shadow-none"
          />
        </>
      }
      filterButtons={
        <div className="flex items-center gap-2">
          {/* Sort Button */}
          <ExpandableButton icon={faArrowUpArrowDown} onClick={() => setSortModalOpen(true)}>
            <p className="text-sm">Sort</p>
          </ExpandableButton>

          {/* Filters */}
          <FilterExpandableButton filterCount={filterCount} onClick={() => setFiltersModalOpen(true)} />
        </div>
      }
      searchBar={
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          onNLSearchExecuted={handleNLSearchExecuted}
          entityType="documents"
        />
      }
      pagination={
        <Pagination
          canGetPrevPage={currentPage > 0}
          canGetNextPage={totalPages !== undefined && currentPage < totalPages - 1}
          prevPage={prevPage}
          nextPage={nextPage}
          currentPage={currentPage}
          noOfPages={totalPages}
          goToPage={goToPage}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
        />
      }
    />
  );
};

export default DocumentEditorToolbar;
